import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  AmplifierData,
  ProgramStatusMessage,
  ProgramStatusResponseMessage,
  HamlibControlRequestMessage,
} from './amplifier';
import { useAmplifierI18n } from '../../i18n/amplifier/useAmplifierI18n';
import { amplifierMqttService } from './AmplifierMqttService';
import { AmplifierModalPortal } from './AmplifierModalPortal';
import { formatHamBandNameFromFrequency } from './hamBandFromFrequency';

/** 功放中间区域：频率/模式/频段选择、输入输出功率、电压电流温度 */
interface TopBarMiddleProps {
  deviceId?: string;
  amplifierData?: AmplifierData | null;
  programStatusMessage?: ProgramStatusMessage | null;
  hamlibControlRequestMessage?: HamlibControlRequestMessage | null;
}

/** 波形条采样点数量（横向滚动，保留最近 N 个点） */
const WAVEFORM_SAMPLE_SIZE = 50;

/** 默认量程用于首屏和无数据场景；实际绘制会基于最近采样点自适应缩放 */
const DEFAULT_WAVE_RANGES = {
  voltage: { min: 24, max: 36 },
  current: { min: 6, max: 14 },
  power: { min: 0, max: 450 },
  temperature: { min: 20, max: 45 },
} as const;

/** 每项波形最小显示窗口，避免极小抖动被无限放大，也避免小幅变化被压平 */
const MIN_WAVE_SPANS = {
  voltage: 1,      // V
  current: 1,      // A
  power: 50,       // W
  temperature: 2,  // °C
} as const;

function getAdaptiveWaveRange(
  samples: number[],
  fallback: { min: number; max: number },
  minSpan: number,
): { min: number; max: number } {
  const finiteSamples = samples.filter(Number.isFinite);
  if (finiteSamples.length === 0) return fallback;

  const sorted = [...finiteSamples].sort((a, b) => a - b);
  const trimCount = sorted.length >= 12 ? Math.floor(sorted.length * 0.1) : 0;
  const visibleSamples = sorted.slice(trimCount, sorted.length - trimCount);
  const rawMin = visibleSamples[0] ?? fallback.min;
  const rawMax = visibleSamples[visibleSamples.length - 1] ?? fallback.max;
  const center = (rawMin + rawMax) / 2;
  const paddedSpan = Math.max((rawMax - rawMin) * 1.35, minSpan);
  const halfSpan = paddedSpan / 2;

  return {
    min: center - halfSpan,
    max: center + halfSpan,
  };
}

/** 将采样点数组按固定量程转为 SVG path d（viewBox 0 0 100 10，左旧右新） */
function samplesToPath(samples: number[], min: number, max: number, viewWidth = 100, viewHeight = 10): string {
  if (samples.length === 0) return `M0,${viewHeight / 2} L${viewWidth},${viewHeight / 2}`;
  const range = max - min || 1;
  const padding = 1;
  const usableHeight = viewHeight - 2 * padding;
  const points = samples.map((v, i) => {
    const x = samples.length === 1 ? viewWidth / 2 : (i / (samples.length - 1)) * viewWidth;
    const clamped = Math.min(max, Math.max(min, v));
    const y = padding + (1 - (clamped - min) / range) * usableHeight;
    return `${x},${y}`;
  });
  return `M${points.join(' L')}`;
}

const TopBarMiddleInner: React.FC<TopBarMiddleProps> = ({ deviceId: propDeviceId, amplifierData, programStatusMessage, hamlibControlRequestMessage }) => {
    const location = useLocation();
    const { t } = useAmplifierI18n();

    // ---------- 派生数据（只读用 Message，下发给设备用 Response 深拷贝） ----------
    /** 仅用于渲染，不修改 */
    const programStatusMessageData = programStatusMessage?.data;
    /** 深拷贝，用于组包下发；不参与渲染 */
    const programStatusResponseMessage: ProgramStatusResponseMessage | null = programStatusMessage
        ? {
            msg_id: programStatusMessage.msg_id,
            cmd: 'update_program_status',
            timestamp: programStatusMessage.timestamp,
            data: {
                ...programStatusMessage.data,
                freq_band_name: programStatusMessage.data.freq_band_name ? [...programStatusMessage.data.freq_band_name] : [],
                freq_band: programStatusMessage.data.freq_band ? [...programStatusMessage.data.freq_band] : [],
                band_voltage: programStatusMessage.data.band_voltage ? [...programStatusMessage.data.band_voltage] : [],
                ...(Array.isArray(programStatusMessage.data.band_voltage_name)
                  ? { band_voltage_name: [...programStatusMessage.data.band_voltage_name] }
                  : {}),
                ...(Array.isArray(programStatusMessage.data.band_voltage_freq)
                  ? { band_voltage_freq: [...programStatusMessage.data.band_voltage_freq] }
                  : {}),
            },
        }
        : null;

    /** 提交编辑时用 ref 保存“最后一次下发/编辑”的 payload，选择频段确认时优先用 ref，避免用旧 props 回滚 */
    const lastProgramStatusResponseRef = useRef<ProgramStatusResponseMessage | null>(null);
    if (programStatusResponseMessage && !lastProgramStatusResponseRef.current) {
        lastProgramStatusResponseRef.current = programStatusResponseMessage;
    }

    // ---------- 状态 ----------
    const [isBandModalVisible, setIsBandModalVisible] = useState(false);
    const [selectedBandIndex, setSelectedBandIndex] = useState(-1);
    const [isEditBandModalVisible, setIsEditBandModalVisible] = useState(false);
    const [isBandVoltageModalVisible, setIsBandVoltageModalVisible] = useState(false);
    const [bandVoltageSelectedIndex, setBandVoltageSelectedIndex] = useState(-1);
    const [editingBandIndex, setEditingBandIndex] = useState(-1);
    const [editingBandName, setEditingBandName] = useState('');
    const [editingBandValue, setEditingBandValue] = useState(0);
    /** 电压/电流/功率/温度 各保留最近 50 个采样点，用于波形条展示 */
    const [sampleBuffers, setSampleBuffers] = useState<{
      voltage: number[];
      current: number[];
      power: number[];
      temperature: number[];
    }>({ voltage: [], current: [], power: [], temperature: [] });

    /** 最新显示值 ref，供定时器按固定间隔写入缓冲，避免 MQTT 高频触发导致卡顿 */
    const latestDisplayValuesRef = useRef({ v: 0, c: 0, p: 0, t: 0 });

    const deviceId = propDeviceId || (location.state as { deviceId?: string })?.deviceId || '1f75d96b40e8ed8e';
    const isManualMode = programStatusMessageData?.freq_mode === 2;
    const normalizeGridIndex = (value: unknown, maxIndex = 15) => {
        if (typeof value === 'number' && Number.isInteger(value)) return value;
        if (typeof value === 'string' && value.trim() !== '') {
            const parsed = Number(value);
            if (Number.isInteger(parsed)) return parsed;
        }
        return -1;
    };
    const currentFreqBandSelectRaw = normalizeGridIndex(programStatusMessageData?.freq_band_select);
    const currentFreqBandSelect =
        currentFreqBandSelectRaw >= 0 && currentFreqBandSelectRaw <= 15 ? currentFreqBandSelectRaw : -1;
    const filterLabelMap: Record<number, string> = {
        1: 'LPF160m', 2: 'LPF80m', 3: 'LPF40m', 4: 'LPF20m',
        5: 'LPF15m/17m', 6: 'LPF10m', 7: 'LPF6m', 8: 'LPF 4m', 9: 'LPF2m'
    };
    const filterLabel = amplifierData?.filterSel
        ?? (amplifierData?.filter != null && filterLabelMap[amplifierData.filter]
            ? filterLabelMap[amplifierData.filter]
            : '—');
    const isRemoteControlData = amplifierData?._source === 'remote_control';
    const measurementDivisor = isRemoteControlData ? 1 : 100;
    const swrDivisor = isRemoteControlData ? 1 : 10;
    const displayVoltage = amplifierData?.currentVoltage !== undefined
        ? amplifierData.currentVoltage / measurementDivisor
        : undefined;
    const displayCurrent = amplifierData?.currentCurrent !== undefined
        ? amplifierData.currentCurrent / measurementDivisor
        : undefined;
    const displayTemperature = amplifierData?.currentTemperature !== undefined
        ? amplifierData.currentTemperature / measurementDivisor
        : undefined;
    const displayPower = displayVoltage !== undefined && displayCurrent !== undefined
        ? displayVoltage * displayCurrent
        : undefined;
    const displayInputSwr = amplifierData?.inputRf !== undefined
        ? amplifierData.inputRf / swrDivisor
        : undefined;
    const displayOutputSwr = amplifierData?.outputRf !== undefined
        ? amplifierData.outputRf / swrDivisor
        : undefined;

    const getBandValueText = (value: unknown) => {
        if (value === null || value === undefined || value === '') return '-';
        return String(value);
    };

    /** amplifierData 更新时只写入 ref，不触发 setState。波形区与天线独立，仅按接收到的数据更新，不做任何与天线相关的特殊处理 */
    useEffect(() => {
        if (!amplifierData) return;
        latestDisplayValuesRef.current = {
            v: displayVoltage ?? 0,
            c: displayCurrent ?? 0,
            p: displayPower ?? 0,
            t: displayTemperature ?? 0,
        };
    }, [amplifierData, displayVoltage, displayCurrent, displayPower, displayTemperature]);

    useEffect(() => {
        if (!programStatusMessageData) return;
        setSelectedBandIndex(currentFreqBandSelect >= 0 ? currentFreqBandSelect : -1);
    }, [programStatusMessageData, currentFreqBandSelect]);

    /** 频段 chip：由 work_freq 自动换算业余习惯波段名，不依赖 freq_band_name 索引 */
    const displayBandLabel = useMemo(
        () => formatHamBandNameFromFrequency(amplifierData?.frequency),
        [amplifierData?.frequency],
    );

    useEffect(() => {
        if (!isBandVoltageModalVisible) return;
        const bandVoltageSelectRaw = normalizeGridIndex(programStatusMessageData?.band_voltage_select);
        const bandVoltageSelect =
            bandVoltageSelectRaw >= 0 && bandVoltageSelectRaw <= 15 ? bandVoltageSelectRaw : -1;
        setBandVoltageSelectedIndex(bandVoltageSelect);
    }, [isBandVoltageModalVisible]);

    /** 按固定间隔（80ms）从 ref 追加到波形缓冲，限制 setState 频率，保证动画流畅 */
    useEffect(() => {
        const interval = setInterval(() => {
            const { v, c, p, t } = latestDisplayValuesRef.current;
            setSampleBuffers((prev) => ({
                voltage: [...prev.voltage, v].slice(-WAVEFORM_SAMPLE_SIZE),
                current: [...prev.current, c].slice(-WAVEFORM_SAMPLE_SIZE),
                power: [...prev.power, p].slice(-WAVEFORM_SAMPLE_SIZE),
                temperature: [...prev.temperature, t].slice(-WAVEFORM_SAMPLE_SIZE),
            }));
        }, 80);
        return () => clearInterval(interval);
    }, []);

    /** 波形量程基于最近采样点自适应，提升小幅变化的可见性 */
    const adaptiveWaveRanges = useMemo(() => ({
      voltage: getAdaptiveWaveRange(sampleBuffers.voltage, DEFAULT_WAVE_RANGES.voltage, MIN_WAVE_SPANS.voltage),
      current: getAdaptiveWaveRange(sampleBuffers.current, DEFAULT_WAVE_RANGES.current, MIN_WAVE_SPANS.current),
      power: getAdaptiveWaveRange(sampleBuffers.power, DEFAULT_WAVE_RANGES.power, MIN_WAVE_SPANS.power),
      temperature: getAdaptiveWaveRange(sampleBuffers.temperature, DEFAULT_WAVE_RANGES.temperature, MIN_WAVE_SPANS.temperature),
    }), [sampleBuffers]);

    /** 波形 path 字符串缓存，避免无关重渲染时重复计算 */
    const pathVoltage = useMemo(
      () => samplesToPath(sampleBuffers.voltage, adaptiveWaveRanges.voltage.min, adaptiveWaveRanges.voltage.max),
      [sampleBuffers.voltage, adaptiveWaveRanges.voltage]
    );
    const pathCurrent = useMemo(
      () => samplesToPath(sampleBuffers.current, adaptiveWaveRanges.current.min, adaptiveWaveRanges.current.max),
      [sampleBuffers.current, adaptiveWaveRanges.current]
    );
    const pathPower = useMemo(
      () => samplesToPath(sampleBuffers.power, adaptiveWaveRanges.power.min, adaptiveWaveRanges.power.max),
      [sampleBuffers.power, adaptiveWaveRanges.power]
    );
    const pathTemperature = useMemo(
      () => samplesToPath(sampleBuffers.temperature, adaptiveWaveRanges.temperature.min, adaptiveWaveRanges.temperature.max),
      [sampleBuffers.temperature, adaptiveWaveRanges.temperature]
    );

    /** 频率 Hz 整数，从右往左每 3 位加小数点，如 1801000 → 1.801.000 */
    const formatHzDotGroups = (freq: number): string => {
        const digits = Math.round(freq).toString();
        return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    };

    /** 频率显示：无数据时显示「频段未获取」，否则显示分组 Hz */
    const formatFrequency = (freq?: number) =>
        freq != null && freq > 0
            ? `${formatHzDotGroups(freq)} Hz`
            : t('频段未获取');

    /** 发送模式切换（0 自动 / 1 协议 / 2 手动 / 3 波段电压），仅组包下发 */
    const sendFreqMode = (mode: 0 | 1 | 2 | 3) => {
        if (!deviceId) return;
        amplifierMqttService.sendProgramStatusDelta(deviceId, { freq_mode: mode }).catch((err) =>
            console.error(`TopBarMiddle: 发送模式指令失败 mode=${mode}`, err)
        );
    };

    /** 选中频段槽位并下发 freq_band_select（不自动关弹窗，需点「关闭」） */
    const applyBandSelection = (index: number) => {
        if (!deviceId || index < 0 || index > 15) return;
        setSelectedBandIndex(index);
        amplifierMqttService
            .sendProgramStatusDelta(deviceId, { freq_band_select: index })
            .catch((err) => console.error('TopBarMiddle: 发送频段选择指令失败', err));
    };

    const openBandOrVoltageModal = () => {
        const freqMode = Number(programStatusMessageData?.freq_mode);
        if (freqMode === 3) {
            setIsBandVoltageModalVisible(true);
        } else if (freqMode === 2) {
            setIsBandModalVisible(true);
        }
    };

    /** 选中波段电压槽位并下发 band_voltage_select（不自动关弹窗，需点「关闭」） */
    const applyBandVoltageSelection = (index: number) => {
        if (!deviceId || index < 0 || index > 15) return;
        setBandVoltageSelectedIndex(index);
        amplifierMqttService
            .sendProgramStatusDelta(deviceId, { band_voltage_select: index })
            .catch((err) => console.error('TopBarMiddle: 发送波段电压选择失败', err));
    };

    return (
        <div style={{ display: 'flex', gap: 0, marginTop: 'var(--spacing-md)', marginLeft: 'var(--spacing-lg)', marginRight: 'var(--spacing-lg)' }}>
            {/* ---------- 频段选择弹窗（16 格；点击格子即下发 freq_band_select） ---------- */}
            <AmplifierModalPortal open={isBandModalVisible}>
                    <div className="amplifier-modal-panel" style={{ width: '600px' }} onClick={(e) => e.stopPropagation()}>
                        <h3 className="amplifier-modal-panel__title">{t('选择频段')}</h3>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: 'var(--spacing-sm)',
                            marginBottom: 'var(--spacing-lg)'
                        }}>
                            {Array.from({ length: 16 }).map((_, index) => {
                                const bandName = programStatusMessageData?.freq_band_name?.[index];
                                const bandValue = programStatusMessageData?.freq_band?.[index];
                                const isSelected = selectedBandIndex === index;
                                return (
                                    <div
                                        key={index}
                                        className={`amplifier-grid-cell${isSelected ? ' amplifier-grid-cell--selected' : ''}`}
                                        style={{ width: '120px', minHeight: '80px' }}
                                        onClick={() => {
                                            if (isSelected) {
                                                setEditingBandIndex(index);
                                                setEditingBandName(bandName || '');
                                                setEditingBandValue(Number(programStatusMessageData?.freq_band?.[index] ?? 0));
                                                setIsEditBandModalVisible(true);
                                            } else {
                                                applyBandSelection(index);
                                            }
                                        }}
                                    >
                                        <span className="amplifier-grid-cell__name">{bandName || '-'}</span>
                                        <span className="amplifier-grid-cell__value">{getBandValueText(bandValue)}</span>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{
                            display: 'flex',
                            gap: 'var(--spacing-md)',
                            justifyContent: 'flex-end'
                        }}>
                            <div className="amp-modal-btn" onClick={() => setIsBandModalVisible(false)}>
                                <span className="amp-modal-btn__text">{t('关闭')}</span>
                            </div>
                        </div>
                    </div>
            </AmplifierModalPortal>

            {/* ---------- 编辑频段弹窗（展示 Message 数据，提交写入 Response 并 sendJsonCommand） ---------- */}
            <AmplifierModalPortal
                open={isEditBandModalVisible}
                layer="elevated"
                onBackdropClick={() => setIsEditBandModalVisible(false)}
            >
                    <div
                        className="amplifier-modal-panel"
                        style={{ width: '400px' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="amplifier-modal-panel__title">{t('编辑频段')}</h3>
                        {/* 提交表单时向 MQTT 发送编辑后的 freq_band_name / freq_band */}
                        <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                if (!deviceId || editingBandIndex < 0) return;
                                const applyEdit = () => {
                                    setIsEditBandModalVisible(false);
                                    setSelectedBandIndex(editingBandIndex);
                                };
                                const source = programStatusResponseMessage ?? lastProgramStatusResponseRef.current;
                                if (source?.data) {
                                    const names = [...(source.data.freq_band_name || [])];
                                    const values = [...(source.data.freq_band || [])];
                                    while (names.length <= editingBandIndex) names.push('');
                                    while (values.length <= editingBandIndex) values.push(0);
                                    names[editingBandIndex] = editingBandName;
                                    values[editingBandIndex] = editingBandValue;
                                    amplifierMqttService
                                        .sendProgramStatusDelta(deviceId, {
                                            freq_band_name: names,
                                            freq_band: values,
                                        })
                                        .then(() => {
                                            if (source) {
                                                lastProgramStatusResponseRef.current = {
                                                    ...source,
                                                    data: {
                                                        ...source.data,
                                                        freq_band_name: names,
                                                        freq_band: values,
                                                    },
                                                };
                                            }
                                            applyEdit();
                                        })
                                        .catch((err) => {
                                            console.error('TopBarMiddle: 发送频段编辑指令失败', err);
                                        });
                                } else {
                                    console.warn('TopBarMiddle: 无 programStatusMessage，无法发送频段编辑到 MQTT');
                                    applyEdit();
                                }
                            }}
                        >
                            <div style={{ marginBottom: 'var(--spacing-lg)' }}>
                                <div style={{ marginBottom: 'var(--spacing-md)' }}>
                                    <span className="amp-modal-field-label">{t('频段名称')}</span>
                                    <input
                                        type="text"
                                        value={editingBandName}
                                        onChange={(e) => setEditingBandName(e.target.value)}
                                        className="amp-modal-input"
                                    />
                                </div>
                                <div style={{ marginBottom: 'var(--spacing-md)' }}>
                                    <span className="amp-modal-field-label">{t('频段值')}</span>
                                    <input
                                        type="number"
                                        value={editingBandValue}
                                        onChange={(e) => setEditingBandValue(Number(e.target.value))}
                                        className="amp-modal-input"
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    className="amp-modal-btn"
                                    onClick={() => setIsEditBandModalVisible(false)}
                                >
                                    <span className="amp-modal-btn__text">{t('取消')}</span>
                                </button>
                                <button
                                    type="submit"
                                    className="amp-modal-btn"
                                >
                                    <span className="amp-modal-btn__text">{t('确认')}</span>
                                </button>
                            </div>
                        </form>
                    </div>
            </AmplifierModalPortal>

            {/* ---------- 波段电压弹窗：点击格子即下发 band_voltage_select ---------- */}
            <AmplifierModalPortal open={isBandVoltageModalVisible}>
                    <div className="amplifier-modal-panel" style={{ width: '600px' }} onClick={(e) => e.stopPropagation()}>
                        <h3 className="amplifier-modal-panel__title">{t('波段电压')}</h3>
                        <div className="amplifier-modal-panel__hint">
                            {t('当前波段电压：')}{programStatusMessageData?.current_band_voltage != null
                                ? getBandValueText(programStatusMessageData.current_band_voltage)
                                : '—'}
                        </div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, 1fr)',
                            gap: 'var(--spacing-sm)',
                            marginBottom: 'var(--spacing-lg)'
                        }}>
                            {Array.from({ length: 16 }).map((_, index) => {
                                const bandName =
                                    programStatusMessageData?.band_voltage_name?.[index] ??
                                    programStatusMessageData?.freq_band_name?.[index];
                                const bandVoltageFreq = programStatusMessageData?.band_voltage_freq?.[index];
                                const bandVoltageValue = programStatusMessageData?.band_voltage?.[index];
                                const hasBandVoltage = bandVoltageValue != null && bandVoltageValue !== -1;
                                const isSelected = bandVoltageSelectedIndex === index;
                                return (
                                    <div
                                        key={index}
                                        className={`amplifier-grid-cell${isSelected ? ' amplifier-grid-cell--selected' : ''}`}
                                        style={{ width: '120px', height: '96px', gap: 4 }}
                                        onClick={() => applyBandVoltageSelection(index)}
                                    >
                                        <span className="amplifier-grid-cell__name">{bandName || '-'}</span>
                                        <span className="amplifier-grid-cell__value" style={{ fontSize: '0.8em' }}>
                                            {getBandValueText(bandVoltageFreq)}
                                        </span>
                                        <span className={`amplifier-grid-cell__sub${hasBandVoltage ? ' amplifier-grid-cell__sub--highlight' : ''}`}>
                                            {t('波段电压:')}{hasBandVoltage ? getBandValueText(bandVoltageValue) : '-'}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--spacing-md)', justifyContent: 'flex-end' }}>
                            <div className="amp-modal-btn" onClick={() => setIsBandVoltageModalVisible(false)}>
                                <span className="amp-modal-btn__text">{t('关闭')}</span>
                            </div>
                        </div>
                    </div>
            </AmplifierModalPortal>

            {/* ---------- 左侧面板：频率、模式（自动/协议/手动/波段电压）、滤波器、频段 ---------- */}
            <div className="amplifier-middle__panel amplifier-middle__panel--left" style={{
                padding: 'var(--spacing-lg)',
                maxWidth: '25%',
                flex: '0 0 25%'
            }}>
                <div className="amplifier-middle__freq-block" style={{
                    textAlign: 'center',
                    marginBottom: 'var(--spacing-lg)'
                }}>
                    <h2 className="amplifier-middle__freq">{formatFrequency(amplifierData?.frequency)}</h2>
                </div>

                <div style={{ display: 'flex', marginBottom: 'var(--spacing-lg)' }}>
                    <div
                        className={`amp-mode-btn amp-mode-btn--readonly${programStatusMessageData?.freq_mode === 0 ? ' amp-mode-btn--active' : ''}`}
                    >
                        <span className="amp-mode-btn__label">{t('自动模式')}</span>
                    </div>
                    <div
                        className={`amp-mode-btn${programStatusMessageData?.freq_mode === 1 ? ' amp-mode-btn--active' : ''}`}
                        onClick={() => {
                            if (programStatusMessageData?.freq_mode !== 1) {
                                sendFreqMode(1);
                            }
                        }}
                    >
                        <span className="amp-mode-btn__label">{t('协议模式')}</span>
                    </div>
                    <div
                        className={`amp-mode-btn${isManualMode ? ' amp-mode-btn--active' : ''}`}
                        onClick={() => sendFreqMode(2)}
                    >
                        <span className="amp-mode-btn__label">{t('手动选择')}</span>
                    </div>
                    <div
                        className={`amp-mode-btn${Number(programStatusMessageData?.freq_mode) === 3 ? ' amp-mode-btn--active' : ''}`}
                        onClick={() => {
                            if (Number(programStatusMessageData?.freq_mode) !== 3) {
                                sendFreqMode(3);
                            }
                        }}
                    >
                        <span className="amp-mode-btn__label">{t('波段电压')}</span>
                    </div>
                </div>

                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                        <span className="amp-field-label">{t('滤波器：')}</span>
                        <span className="amp-field-value">{filterLabel}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                        <span className="amp-field-label">{t('频段：')}</span>
                        <div className="amp-chip-btn" onClick={openBandOrVoltageModal}>
                            <span className="amp-chip-btn__text">{displayBandLabel}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="amplifier-middle__panel" style={{
                padding: 'var(--spacing-lg)',
                flex: '0 0 50%'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-around',
                    marginBottom: 'var(--spacing-md)'
                }}>
                    <h3 className="amp-metric-title">{t('输入')}</h3>
                    <h3 className="amp-metric-title">{t('输出')}</h3>
                </div>

                <div style={{ display: 'flex', gap: 'var(--spacing-lg)' }}>
                    <div style={{ flex: 1 }}>
                        <div className="amp-metric-primary">
                            <span className="amp-metric-primary__value">{amplifierData?.inputPowerPositive !== undefined ? `${amplifierData.inputPowerPositive.toFixed(1)} W` : '--'}</span>
                        </div>

                        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)' }}>
                            <div className="amp-metric-sub">
                                <div className="amp-metric-sub__label">{t('反向功率')}</div>
                                <span className="amp-metric-sub__value">{amplifierData?.inputPowerNegative !== undefined ? `${amplifierData.inputPowerNegative.toFixed(1)} W` : '--'}</span>
                            </div>
                            <div className="amp-metric-sub">
                                <div className="amp-metric-sub__label">{t('驻波')}</div>
                                <span className="amp-metric-sub__value">{displayInputSwr !== undefined ? displayInputSwr.toFixed(1) : '--'}</span>
                            </div>
                        </div>

                        <div style={{ marginBottom: 'var(--spacing-xs)' }}>
                            <span className="amp-metric-sub__label">{t('驻波')}</span>
                        </div>
                        <div style={{ display: 'flex', height: '10px', gap: '2px', position: 'relative' }}>
                            {(displayInputSwr == null || displayInputSwr <= 0) ? (
                                <div className="amp-swr-track" style={{ borderRadius: '3px' }} />
                            ) : (
                                <>
                                    <div style={{ flex: '0 0 10%' }} className="amp-swr-zone--good" />
                                    <div style={{
                                        flex: `0 0 ${displayInputSwr > 1 ? Math.min(((displayInputSwr - 1) / 2) * 60, 60) : 0}%`
                                    }} className="amp-swr-zone--warn" />
                                    <div style={{
                                        flex: `0 0 ${displayInputSwr > 3 ? Math.min(((displayInputSwr - 3) / 21) * 30, 30) : 0}%`
                                    }} className="amp-swr-zone--bad" />
                                    <div className="amp-swr-track" style={{ minWidth: 0 }} />
                                    <div
                                        className="amp-swr-marker"
                                        style={{
                                            position: 'absolute',
                                            left: `${Math.min((displayInputSwr / 24) * 100, 100)}%`,
                                            top: '-50%',
                                            width: '4px',
                                            height: '20px',
                                            transform: 'translateX(-50%)',
                                        }}
                                    />
                                </>
                            )}
                            <div className="amp-swr-tick" style={{ position: 'absolute', left: '10%', top: '100%', marginTop: '2px' }}>1</div>
                            <div className="amp-swr-tick" style={{ position: 'absolute', left: '70%', top: '100%', marginTop: '2px' }}>3</div>
                            <div className="amp-swr-tick" style={{ position: 'absolute', left: '98%', top: '100%', marginTop: '2px' }}>24</div>
                        </div>
                    </div>

                    <div style={{ flex: 1 }}>
                        <div className="amp-metric-primary">
                            <span className="amp-metric-primary__value">{amplifierData?.outputPowerPositive !== undefined ? `${amplifierData.outputPowerPositive.toFixed(1)} W` : '--'}</span>
                        </div>

                        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-md)' }}>
                            <div className="amp-metric-sub">
                                <div className="amp-metric-sub__label">{t('反向功率')}</div>
                                <span className="amp-metric-sub__value">{amplifierData?.outputPowerNegative !== undefined ? `${amplifierData.outputPowerNegative.toFixed(1)} W` : '--'}</span>
                            </div>
                            <div className="amp-metric-sub">
                                <div className="amp-metric-sub__label">{t('驻波')}</div>
                                <span className="amp-metric-sub__value">{displayOutputSwr !== undefined ? displayOutputSwr.toFixed(1) : '--' }</span>
                            </div>
                        </div>

                        <div style={{ marginBottom: 'var(--spacing-xs)' }}>
                            <span className="amp-metric-sub__label">{t('驻波')}</span>
                        </div>
                        <div style={{ display: 'flex', height: '10px', gap: '2px', position: 'relative' }}>
                            {(displayOutputSwr == null || displayOutputSwr <= 0) ? (
                                <div className="amp-swr-track" style={{ borderRadius: '3px' }} />
                            ) : (
                                <>
                                    <div style={{ flex: '0 0 10%' }} className="amp-swr-zone--good" />
                                    <div style={{
                                        flex: `0 0 ${displayOutputSwr > 1 ? Math.min(((displayOutputSwr - 1) / 2) * 60, 60) : 0}%`
                                    }} className="amp-swr-zone--warn" />
                                    <div style={{
                                        flex: `0 0 ${displayOutputSwr > 3 ? Math.min(((displayOutputSwr - 3) / 21) * 30, 30) : 0}%`
                                    }} className="amp-swr-zone--bad" />
                                    <div className="amp-swr-track" style={{ minWidth: 0 }} />
                                    <div
                                        className="amp-swr-marker"
                                        style={{
                                            position: 'absolute',
                                            left: `${Math.min((displayOutputSwr / 24) * 100, 100)}%`,
                                            top: '-50%',
                                            width: '4px',
                                            height: '20px',
                                            transform: 'translateX(-50%)',
                                        }}
                                    />
                                </>
                            )}
                            <div className="amp-swr-tick" style={{ position: 'absolute', left: '10%', top: '100%', marginTop: '2px' }}>1</div>
                            <div className="amp-swr-tick" style={{ position: 'absolute', left: '70%', top: '100%', marginTop: '2px' }}>3</div>
                            <div className="amp-swr-tick" style={{ position: 'absolute', left: '98%', top: '100%', marginTop: '2px' }}>24</div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="amplifier-middle__panel" style={{
                padding: 'var(--spacing-lg)',
                flex: '0 0 25%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
            }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div className="amp-telemetry-row">
                        <span className="amp-telemetry-label">{t('电压:')}</span>
                        <span className="amp-telemetry-value">{displayVoltage !== undefined ? `${displayVoltage.toFixed(1)} V` : '--'}</span>
                    </div>
                    <div style={{ marginTop: 'var(--spacing-xs)', height: '10px' }}>
                        <svg width="100%" height="100%" viewBox="0 0 100 10" preserveAspectRatio="none">
                            <path
                                d={pathVoltage}
                                className="amp-telemetry-wave"
                                strokeWidth="2"
                                fill="none"
                                vectorEffect="non-scaling-stroke"
                            />
                        </svg>
                    </div>
                </div>
                <div className="amp-divider" style={{ height: '1px' }} />

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div className="amp-telemetry-row">
                        <span className="amp-telemetry-label">{t('电流:')}</span>
                        <span className="amp-telemetry-value">{displayCurrent !== undefined ? `${displayCurrent.toFixed(1)} A` : '--'}</span>
                    </div>
                    <div style={{ marginTop: 'var(--spacing-xs)', height: '10px' }}>
                        <svg width="100%" height="100%" viewBox="0 0 100 10" preserveAspectRatio="none">
                            <path
                                d={pathCurrent}
                                className="amp-telemetry-wave"
                                strokeWidth="2"
                                fill="none"
                                vectorEffect="non-scaling-stroke"
                            />
                        </svg>
                    </div>
                </div>
                <div className="amp-divider" style={{ height: '2px' }} />

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div className="amp-telemetry-row">
                        <span className="amp-telemetry-label">{t('功率:')}</span>
                        <span className="amp-telemetry-value">{displayPower !== undefined ? `${displayPower.toFixed(1)} W` : '--'}</span>
                    </div>
                    <div style={{ marginTop: 'var(--spacing-xs)', height: '10px' }}>
                        <svg width="100%" height="100%" viewBox="0 0 100 10" preserveAspectRatio="none">
                            <path
                                d={pathPower}
                                className="amp-telemetry-wave"
                                strokeWidth="2"
                                fill="none"
                                vectorEffect="non-scaling-stroke"
                            />
                        </svg>
                    </div>
                </div>
                <div className="amp-divider" style={{ height: '1px' }} />

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div className="amp-telemetry-row">
                        <span className="amp-telemetry-label">{t('温度:')}</span>
                        <span className="amp-telemetry-value">{displayTemperature !== undefined ? `${displayTemperature.toFixed(1)} °C` : '--'}</span>
                    </div>
                    <div style={{ marginTop: 'var(--spacing-xs)', height: '10px' }}>
                        <svg width="100%" height="100%" viewBox="0 0 100 10" preserveAspectRatio="none">
                            <path
                                d={pathTemperature}
                                className="amp-telemetry-wave"
                                strokeWidth="2"
                                fill="none"
                                vectorEffect="non-scaling-stroke"
                            />
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    );
};

/** 使用 memo 避免父组件因天线等无关 state 更新时重渲染，防止“整页刷新”感 */
export const TopBarMiddle = React.memo(TopBarMiddleInner);
export default TopBarMiddle;